import Component from "@glimmer/component";
import { service } from "@ember/service";
import { registerDestructor, isDestroying, isDestroyed } from "@ember/destroyable";
import type Owner from "@ember/owner";
import { modifier } from "ember-modifier";
import type AudioMotionAnalyzer from "audiomotion-analyzer";
import type AudioService from "#services/audio.ts";

export type VisualizerMode = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 10;
export type VisualizerGradient = "classic" | "prism" | "rainbow" | "orangered" | "steelblue";

export interface AudioVisualizerSignature {
  Element: HTMLDivElement;
  Args: {
    mode?: VisualizerMode;
    gradient?: string;
  };
}

export default class AudioVisualizerComponent extends Component<AudioVisualizerSignature> {
  @service declare audio: AudioService;
  private analyzer: AudioMotionAnalyzer | null = null;
  private isInitializing = false;
  private currentMode?: VisualizerMode;
  private currentGradient?: string;

  constructor(owner: Owner, args: AudioVisualizerSignature["Args"]) {
    super(owner, args);
    registerDestructor(this, () => {
      try {
        this.analyzer?.destroy();
      } catch {
        // cleanup errors ignored
      }
      this.analyzer = null;
    });
  }

  private setupVisualizer = modifier(
    (element: HTMLElement, [mode, gradient]: [VisualizerMode?, string?]) => {
      // Store latest args so async init picks up any changes
      this.currentMode = mode;
      this.currentGradient = gradient;

      if (this.analyzer) {
        // Args change: update existing analyzer instance properties on re-render
        if (mode !== undefined && this.analyzer.mode !== mode) {
          this.analyzer.mode = mode;
        }
        if (gradient !== undefined && this.analyzer.gradient !== gradient) {
          this.analyzer.gradient = gradient;
        }
      } else if (!this.isInitializing) {
        // First run: load and initialize the analyzer asynchronously
        this.isInitializing = true;

        void (async () => {
          try {
            // Dynamically import the module to keep it out of the main bundle
            const { default: AudioMotionAnalyzerClass } = await import("audiomotion-analyzer");

            // Abort if component was destroyed or element unmounted while loading
            if (isDestroying(this) || isDestroyed(this) || !element.isConnected) {
              return;
            }

            const context = this.audio.getContext();
            if (context && typeof context.createAnalyser === "function") {
              const masterGain = this.audio.getMasterGainNode();
              // Mount analyzer to element and tap into the master gain audio stream
              this.analyzer = new AudioMotionAnalyzerClass(element, {
                audioCtx: context,
                source: masterGain,
                connectSpeakers: false,
                mode: this.currentMode ?? 1,
                gradient: this.currentGradient ?? "classic",
                showScaleX: false,
                showScaleY: false,
                showFPS: false,
                showPeaks: true,
                bgAlpha: 0,
              });
            }
          } catch (err) {
            console.warn("Failed to initialize AudioMotionAnalyzer:", err);
          } finally {
            this.isInitializing = false;
          }
        })();
      } else {
        // Options changed during dynamic init; safe no-op
      }

      // Cleanup analyzer when the element is removed from the DOM
      return () => {
        if (!element.isConnected) {
          try {
            this.analyzer?.destroy();
          } catch {
            // cleanup errors ignored
          }
          this.analyzer = null;
        }
      };
    },
  );

  <template>
    <div
      class="w-full relative overflow-hidden bg-neutral-900/90 rounded-lg border border-neutral-800 shadow-inner"
      data-test-audio-visualizer
      {{this.setupVisualizer @mode @gradient}}
      ...attributes
    ></div>
  </template>
}
